import { useParams, Link, Navigate } from "react-router-dom";
import Seo from "@/components/Seo";
import { Eyebrow, Reveal, Section } from "@/components/Primitives";
import { CtaBanner } from "@/components/Blocks";
import { getClient, clientName, TESTIMONIALS } from "@/lib/data";

export default function WorkDetail() {
    const { slug } = useParams();
    const c = getClient(slug);
    if (!c) return <Navigate to="/work" replace />;

    const name = clientName(c);
    const heading = c.project ? `${c.project} · ${name}` : name;
    const t =
        typeof c.testimonial === "number" ? TESTIMONIALS[c.testimonial] : null;

    return (
        <>
            <Seo
                title={heading}
                description={c.summary}
                path={`/work/${c.slug}`}
            />

            <Section className="pt-16 md:pt-24 pb-10">
                <Reveal>
                    <Link
                        to="/work"
                        className="font-mono text-sm text-pa-blue-700 hover:text-pa-blue-600"
                        data-testid="back-to-work"
                    >
                        ← All case studies
                    </Link>
                </Reveal>
                <Reveal delay={0.05}>
                    <div className="mt-8 flex flex-wrap gap-3 font-mono text-xs uppercase tracking-widest text-pa-ink-500">
                        <span className="text-pa-blue-700">{c.sector}</span>
                        <span>·</span>
                        <span>{c.region}</span>
                        <span>·</span>
                        <span>{c.duration}</span>
                    </div>
                    <h1 className="mt-4 font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
                        {heading}
                    </h1>
                    <p className="mt-6 text-lg md:text-xl text-pa-ink-500 max-w-3xl leading-relaxed">
                        {c.summary}
                    </p>
                </Reveal>

                {c.image && (
                    <Reveal delay={0.1}>
                        <figure className="group relative mt-12 cut-tr-lg overflow-hidden bg-pa-ink-900 aspect-[16/9] md:aspect-[21/9]">
                            <img
                                src={c.image}
                                alt={c.imageAlt || heading}
                                loading="lazy"
                                decoding="async"
                                className="h-full w-full object-cover object-center opacity-95 transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                            />
                            {/* deliberate spotlight treatment (photo, not decorative UI gradient) */}
                            <span
                                aria-hidden="true"
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "radial-gradient(70% 90% at 22% 30%, rgba(0,131,255,0.12), transparent 55%), radial-gradient(90% 120% at 80% 100%, rgba(10,21,36,0.75), transparent 60%)",
                                }}
                            />
                            <span
                                aria-hidden="true"
                                className="absolute top-0 right-0 h-12 w-12 bg-pa-paper"
                                style={{
                                    clipPath: "polygon(100% 0, 0 0, 100% 100%)",
                                }}
                            />
                            <figcaption className="absolute left-0 bottom-0 p-5 md:p-6 flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-widest text-white/85">
                                <span className="step-tick" aria-hidden="true" />
                                {c.region} · {c.sector}
                            </figcaption>
                        </figure>
                    </Reveal>
                )}
            </Section>

            <Section className="pt-0">
                <div className="grid gap-12 md:grid-cols-3">
                    {[
                        ["The problem", c.problem],
                        ["What we built", c.solution],
                        ["The outcome", c.impact],
                    ].map(([label, body], i) => (
                        <Reveal key={label} delay={i * 0.08}>
                            <div>
                                <Eyebrow>{label}</Eyebrow>
                                <p className="mt-4 text-pa-ink-900 leading-relaxed">
                                    {body}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>

                {(c.channels || c.integrations) && (
                    <Reveal>
                        <div className="mt-14 border-t border-pa-line pt-8 flex flex-wrap gap-x-16 gap-y-6">
                            {c.channels && (
                                <div>
                                    <div className="font-mono text-xs uppercase tracking-widest text-pa-ink-500">
                                        Channels
                                    </div>
                                    <div className="mt-2 font-display text-xl font-bold">
                                        {c.channels.join(" · ")}
                                    </div>
                                </div>
                            )}
                            {c.integrations && (
                                <div>
                                    <div className="font-mono text-xs uppercase tracking-widest text-pa-ink-500">
                                        Integrations
                                    </div>
                                    <div className="mt-2 font-display text-xl font-bold">
                                        {c.integrations.join(" · ")}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Reveal>
                )}
            </Section>

            {t && (
                <Section dark>
                    <Reveal>
                        <figure className="max-w-3xl">
                            <blockquote className="font-display text-2xl md:text-3xl font-bold leading-snug">
                                {t.quote}
                            </blockquote>
                            <figcaption className="mt-6 font-mono text-sm text-pa-blue-050/70">
                                <span className="text-white">{t.name}</span> —{" "}
                                {t.role}
                            </figcaption>
                        </figure>
                    </Reveal>
                </Section>
            )}

            <CtaBanner />
        </>
    );
}
